import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { di } from '../../../state/inversify.config';
import { ListTeamsQueryParamService } from './listTeams.queryParam.service';
import { Team } from '../../../state/reduxRepositories/teams/team.interfaces';
import { TeamSummaryCard } from './components/teamSummaryCard.smart';
import { ListTeamDomainService } from './listTeamsDomainService';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import { ManageTeam } from '../team/manageTeam';
import { Role } from '../../../state/reduxRepositories/users/users.interfaces';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { Button } from '../../../components/button/button';

interface TeamListProps {
  size: 'small' | 'large';
}

export function TeamList({ size }: TeamListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const location = useLocation();
  const { pathname } = useLocation();
  const [isAdd, setIsAdd] = useState(true);

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const listTeamDomainService = di.get<ListTeamDomainService>(
    ListTeamDomainService,
  );

  const teamsPageQueryService = di.get<ListTeamsQueryParamService>(
    ListTeamsQueryParamService,
  );

  const user = listTeamDomainService.useUserSelector();
  const isTeamPage = user
    ? pathname.includes(`${getRouteByAccountType(user.accountType)}/teams`)
    : false;
  const teams = isTeamPage
    ? listTeamDomainService.useTeamsWithManagerSelector()
    : listTeamDomainService.useTeamsHomePageWithManagerSelector();
  const queryParam = listTeamDomainService.useTeamQueryServiceSelector();
  const players = listTeamDomainService.usePlayerResourceSelector();
  const findTeam = teams?.find((team) => team.id === queryParam.selectedTeamId);
  const [showModal, setShowModal] = useState(false);

  const dispatchSelectedTeam = (team: Team) => {
    teamsPageQueryService.dispatchQueryParams({
      ...queryParam,
      selectedTeamId: team.id,
    });
  };

  const selectTeam = (team: Team) => {
    dispatchSelectedTeam(team);
    navigateTeamDetails(team);
  };

  const navigateTeamDetails = (team: Team) => {
    navigate(
      `/${getRouteByAccountType(
        user.accountType,
      )}/team-details?selectedTeamId=${team.id}`,
    );
  };

  const onRemoveTeam = (id: string, playerIds: string[], teamName: string) => {
    setIsAdd(true);
    if (playerIds.length > 0) {
      setShowConfirmationModal(true);
    } else {
      listTeamDomainService.dispatchRemoveTeam(id);
    }
  };

  const onRemoveWithConfirmation = () => {
    if (queryParam?.selectedTeamId)
      listTeamDomainService.dispatchRemoveTeam(queryParam.selectedTeamId);
    setShowConfirmationModal(false);
  };

  const updateTeam = (team: Team): void => {
    setIsAdd(false);
    setShowModal(true);
  };

  return (
    <Container>
      <Typography
        id="team-title"
        variant="h5"
        component="h5"
        fontWeight="bold"
        sx={{ my: 1 }}
      >
        {t('TEAMS')}
      </Typography>
      {teams.length <= 0 && (
        <Typography sx={{ my: 1 }}>{t('NO_TEAMS')}</Typography>
      )}
      {isTeamPage && user?.role.includes(Role.ADMIN) && (
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => {
            setShowModal(true);
            setIsAdd(true);
          }}
          size="large"
          sx={{ my: 2 }}
          testId="createNewTeam"
          label={t('CREATE_NEW_TEAM')}
          startIcon={<AddIcon />}
        />
      )}
      <Grid container spacing={2}>
        {teams?.map((team) => (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={size === 'small' ? 8 : 4}
            key={team.id}
          >
            <TeamSummaryCard
              currentLogInUser={user}
              team={team}
              players={players}
              updateTeam={updateTeam}
              onRemoveTeam={onRemoveTeam}
              onEditTeam={updateTeam}
              onClick={() => selectTeam(team)}
              dispatchSelectedTeam={dispatchSelectedTeam}
            />
          </Grid>
        ))}

        <AlertDialog
          title={isAdd ? t('CREATE_NEW_TEAM') : t('UPDATE_TEAM_NAME')}
          open={showModal}
          setOpen={setShowModal}
          content={
            <ManageTeam
              isAdd={isAdd}
              selectedTeam={findTeam}
              setShowModal={setShowModal}
            />
          }
        />

        <AlertDialog
          title={t('DELETE_TEAM', { teamName: findTeam?.name })}
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          content={t('DELETE_TEAM_CONTENT', {
            teamName: findTeam?.name,
          })}
          actions
          onConfirm={onRemoveWithConfirmation}
          confirmLabel={t('CONFIRM')}
          cancelLabel={t('CANCEL')}
          onDismiss={() => setShowConfirmationModal(false)}
        />
      </Grid>

      <AlertDialog
        title={t('DELETE_TEAM', { teamName: findTeam?.name })}
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        content={t('DELETE_TEAM_CONTENT', {
          teamName: findTeam?.name,
        })}
        actions
        onConfirm={onRemoveWithConfirmation}
        confirmLabel={t('CONFIRM')}
        cancelLabel={t('CANCEL')}
        onDismiss={() => setShowConfirmationModal(false)}
      />
    </Container>
  );
}
